import React, { useCallback, useEffect, useState } from "react";
import { adminApi, resolveImage } from "../../lib/admin-api";
import { Plus, X, Edit2, Trash2, Eye, EyeOff, Upload, RefreshCw, Shuffle } from "lucide-react";

const emptyForm = {
  client_label_nl: "", client_label_en: "",
  room_type_nl: "", room_type_en: "",
  testimonial_nl: "", testimonial_en: "",
  image_url: "", order: 99, visible: true,
};

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await adminApi.get("/projects");
    setItems(r.data.items || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (it) => {
    await adminApi.patch(`/projects/${it.slug}`, { visible: !it.visible });
    load();
  };
  const remove = async (it) => {
    if (!window.confirm(`Verwijderen: ${it.client_label_nl}?`)) return;
    await adminApi.delete(`/projects/${it.slug}`);
    load();
  };

  return (
    <div data-testid="admin-projects" className="max-w-5xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-stoel-walnut mb-3">— Inhoud</div>
          <h1 className="stoel-display text-4xl">Recente projecten</h1>
        </div>
        <button data-testid="project-add-new" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 px-5 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta">
          <Plus size={16} /> Nieuw project
        </button>
      </div>

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.slug} data-testid={`project-row-${p.slug}`} className="bg-stoel-paper border border-stoel-line p-4 flex gap-4 items-center">
            <img src={resolveImage(p.image_url)} alt="" className="w-24 h-16 object-cover bg-stoel-paperDeep" />
            <div className="flex-1">
              <div className="text-stoel-ink">{p.client_label_nl}</div>
              <div className="text-xs text-stoel-walnut uppercase tracking-[0.2em] mt-1">{p.room_type_nl}</div>
              <div className="text-xs text-stoel-inkSoft line-clamp-1 mt-1 italic">&ldquo;{p.testimonial_nl}&rdquo;</div>
            </div>
            <button onClick={() => toggleVisible(p)} className={`text-xs ${p.visible ? "text-stoel-ink" : "text-stoel-inkSoft/60"}`} data-testid={`project-toggle-${p.slug}`}>
              {p.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button data-testid={`project-edit-${p.slug}`} onClick={() => setEditing(p.slug)} className="text-stoel-ink hover:text-stoel-terracotta p-1"><Edit2 size={16} /></button>
            <button data-testid={`project-delete-${p.slug}`} onClick={() => remove(p)} className="text-stoel-ink hover:text-stoel-terracotta p-1"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <ProjectEditDrawer
          slug={editing === "new" ? null : editing}
          initial={editing === "new" ? null : items.find((x) => x.slug === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function ProjectEditDrawer({ slug, initial, onClose, onSaved }) {
  const isNew = !slug;
  const [form, setForm] = useState(initial ? { ...emptyForm, ...initial } : emptyForm);
  const [altUrl, setAltUrl] = useState(initial?.image_url_alt || "");
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const swapImage = async () => {
    if (!slug) return;
    setImgBusy(true); setError("");
    try {
      const r = await adminApi.post(`/projects/${slug}/swap-image`);
      set("image_url", r.data.image_url);
      setAltUrl(r.data.image_url_alt || "");
    } catch (err) { setError(err?.response?.data?.detail || "Wisselen mislukt"); }
    finally { setImgBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImgBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Upload mislukt"); }
    finally { setImgBusy(false); }
  };

  const regen = async () => {
    setImgBusy(true); setError("");
    try {
      const ctx = `a ${form.room_type_en || form.room_type_nl || "room"} for the client "${form.client_label_en || form.client_label_nl || "anonymous"}"`;
      const r = await adminApi.post("/images/generate", { prompt_context: ctx, kind: "project", out_slug: `project_${(slug || "new")}_${Date.now()}` });
      set("image_url", r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Beeld genereren mislukt"); }
    finally { setImgBusy(false); }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form, order: parseInt(form.order, 10) || 99 };
      if (isNew) await adminApi.post("/projects", payload);
      else await adminApi.patch(`/projects/${slug}`, payload);
      onSaved();
    } catch (err) { setError(err?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="project-edit-drawer" className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-stoel-ink/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-stoel-paper overflow-y-auto p-8 md:p-10 shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <h2 className="stoel-display text-3xl">{isNew ? "Nieuw project" : "Bewerken"}</h2>
          <button onClick={onClose} className="text-stoel-inkSoft hover:text-stoel-ink"><X /></button>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Client label NL"><input data-testid="project-label-nl" value={form.client_label_nl} onChange={(e) => set("client_label_nl", e.target.value)} className={inputCls} /></Field>
            <Field label="Client label EN"><input data-testid="project-label-en" value={form.client_label_en} onChange={(e) => set("client_label_en", e.target.value)} className={inputCls} /></Field>
            <Field label="Room type NL"><input data-testid="project-room-nl" value={form.room_type_nl} onChange={(e) => set("room_type_nl", e.target.value)} className={inputCls} /></Field>
            <Field label="Room type EN"><input data-testid="project-room-en" value={form.room_type_en} onChange={(e) => set("room_type_en", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Testimonial NL"><textarea data-testid="project-testimonial-nl" rows={3} value={form.testimonial_nl} onChange={(e) => set("testimonial_nl", e.target.value)} className={inputCls} /></Field>
          <Field label="Testimonial EN"><textarea data-testid="project-testimonial-en" rows={3} value={form.testimonial_en} onChange={(e) => set("testimonial_en", e.target.value)} className={inputCls} /></Field>

          <Field label="Beeld">
            <div className="flex items-start gap-3">
              {form.image_url && <img src={resolveImage(form.image_url)} alt="" className="w-24 h-16 object-cover bg-stoel-paperDeep border border-stoel-line" />}
              <div className="flex flex-col gap-2 flex-1">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-ink text-sm cursor-pointer hover:bg-stoel-ink hover:text-stoel-paper">
                  <Upload size={14} /> Upload
                  <input type="file" accept="image/*" hidden onChange={upload} />
                </label>
                <button data-testid="project-regen-image" onClick={regen} disabled={imgBusy} className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-line text-sm hover:bg-stoel-paperDeep disabled:opacity-50">
                  <RefreshCw size={14} /> {imgBusy ? "Bezig…" : "Regenereer AI"}
                </button>
                {altUrl && slug && (
                  <button
                    data-testid={`project-swap-alt-${slug}`}
                    onClick={swapImage}
                    disabled={imgBusy}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-terracotta text-stoel-terracotta text-sm hover:bg-stoel-terracotta hover:text-stoel-paper disabled:opacity-50"
                  >
                    <Shuffle size={14} /> Wissel naar variant B
                  </button>
                )}
              </div>
              {altUrl && (
                <div className="flex flex-col items-center gap-1">
                  <img
                    src={resolveImage(altUrl)}
                    alt="Alternate"
                    className="w-20 h-12 object-cover bg-stoel-paperDeep border border-dashed border-stoel-walnut opacity-70"
                  />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-stoel-walnut">Alt</span>
                </div>
              )}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Volgorde (order)"><input data-testid="project-order" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={inputCls} /></Field>
            <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} data-testid="project-visible" /><span className="text-sm">Zichtbaar</span></label>
          </div>

          {error && <div className="bg-stoel-terracotta/10 border-l-2 border-stoel-terracotta p-3 text-sm">{error}</div>}

          <div className="pt-4 flex gap-3">
            <button data-testid="project-save" onClick={save} disabled={saving} className="px-6 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta disabled:opacity-50">{saving ? "Opslaan…" : "Opslaan"}</button>
            <button onClick={onClose} className="px-5 py-2.5 border border-stoel-line text-sm hover:bg-stoel-paperDeep">Annuleren</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-stoel-paper border border-stoel-line px-3 py-2 text-sm focus:outline-none focus:border-stoel-ink";
function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-1.5">{label}</span>{children}</label>;
}
